import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  $createParagraphNode,
  ElementFormatType,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $isHeadingNode, $createHeadingNode, HeadingTagType } from '@lexical/rich-text';
import { $setBlocksType, $patchStyleText } from '@lexical/selection';

import { INSERT_PAGE_BREAK_COMMAND, $insertPageBreak } from './nodes/PageBreakNode';
import { generateResumeContent } from './utils/generateResumeContent';
import './EditorToolbar.css';

const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Times New Roman', value: 'Times New Roman, serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Courier New', value: 'Courier New, monospace' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
  { label: 'Garamond', value: 'Garamond, serif' },
  { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
];

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

const BLOCK_TYPES = [
  { label: 'Normal', value: 'paragraph', fontSize: '11pt', fontWeight: 'normal' },
  { label: 'Heading 1', value: 'h1', fontSize: '24pt', fontWeight: 'bold' },
  { label: 'Heading 2', value: 'h2', fontSize: '18pt', fontWeight: 'bold' },
  { label: 'Heading 3', value: 'h3', fontSize: '14pt', fontWeight: 'bold' },
];

const TEXT_COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
  '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff',
  '#9900ff', '#ff00ff', '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3',
  '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc', '#dd7e6b', '#ea9999', '#f9cb9c', '#ffe599',
];

const HIGHLIGHT_COLORS = [
  'transparent', '#ffff00', '#00ff00', '#00ffff', '#ff00ff', '#ff0000', '#0000ff', '#000080',
  '#008080', '#008000', '#800080', '#800000', '#808000', '#808080', '#c0c0c0', '#ff9900',
];

const LINE_SPACING_OPTIONS = [
  { label: 'Single', value: '1' },
  { label: '1.15', value: '1.15' },
  { label: '1.5', value: '1.5' },
  { label: 'Double', value: '2' },
  { label: '2.5', value: '2.5' },
  { label: '3', value: '3' },
];

const EditorToolbar: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isBulletList, setIsBulletList] = useState(false);
  const [isNumberedList, setIsNumberedList] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState('paragraph');
  const [fontFamily, setFontFamily] = useState('Arial, sans-serif');
  const [fontSize, setFontSize] = useState(11);
  const [textColor, setTextColor] = useState('#000000');
  const [highlightColor, setHighlightColor] = useState('transparent');
  const [elementFormat, setElementFormat] = useState<ElementFormatType>('left');
  const [lineSpacing, setLineSpacing] = useState('1.15');

  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [showSizeDropdown, setShowSizeDropdown] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showLineSpacingDropdown, setShowLineSpacingDropdown] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const blockDropdownRef = useRef<HTMLDivElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const textColorRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const lineSpacingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(e.target as Node)) {
        setShowBlockDropdown(false);
      }
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node)) {
        setShowFontDropdown(false);
      }
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(e.target as Node)) {
        setShowSizeDropdown(false);
      }
      if (textColorRef.current && !textColorRef.current.contains(e.target as Node)) {
        setShowTextColorPicker(false);
      }
      if (highlightRef.current && !highlightRef.current.contains(e.target as Node)) {
        setShowHighlightPicker(false);
      }
      if (lineSpacingRef.current && !lineSpacingRef.current.contains(e.target as Node)) {
        setShowLineSpacingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        if ($isListNode(parentList)) {
          const listType = parentList.getListType();
          setIsBulletList(listType === 'bullet');
          setIsNumberedList(listType === 'number');
        } else {
          setIsBulletList(false);
          setIsNumberedList(false);
        }
      }

      if ($isHeadingNode(element)) {
        setBlockType(element.getTag());
      } else if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        if (parentList) {
          setBlockType(parentList.getListType() === 'bullet' ? 'ul' : 'ol');
        }
      } else {
        setBlockType('paragraph');
      }

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));

      if ('getFormatType' in element && typeof element.getFormatType === 'function') {
        setElementFormat(element.getFormatType() as ElementFormatType);
      } else {
        setElementFormat('left');
      }

      // Extract font size from selected text
      if ($isTextNode(node)) {
        const style = node.getStyle();
        const fontSizeMatch = style.match(/font-size:\s*(\d+)pt/);
        if (fontSizeMatch) {
          setFontSize(parseInt(fontSizeMatch[1], 10));
        } else {
          // Check if it's a heading and set appropriate default size
          if ($isHeadingNode(element)) {
            const tag = element.getTag();
            if (tag === 'h1') setFontSize(24);
            else if (tag === 'h2') setFontSize(18);
            else if (tag === 'h3') setFontSize(14);
            else setFontSize(11);
          } else {
            setFontSize(11); // Default font size
          }
        }
      } else {
        // For non-text nodes, check heading type
        if ($isHeadingNode(element)) {
          const tag = element.getTag();
          if (tag === 'h1') setFontSize(24);
          else if (tag === 'h2') setFontSize(18);
          else if (tag === 'h3') setFontSize(14);
          else setFontSize(11);
        } else {
          setFontSize(11);
        }
      }
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatBold = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  };

  const formatItalic = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  };

  const formatUnderline = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
  };

  const formatStrikethrough = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
  };

  const formatBulletList = () => {
    if (isBulletList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (isNumberedList) {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  const insertLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      const url = prompt('Enter URL:');
      if (url) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      }
    }
  };

  const insertPageBreak = () => {
    editor.update(() => {
      $insertPageBreak();
    });
  };

  const handleResetClick = () => {
    setShowResetConfirm(true);
  };

  const handleResetCancel = () => {
    setShowResetConfirm(false);
  };

  const handleResetConfirm = async () => {
    setShowResetConfirm(false);
    try {
      const templateResult = await generateResumeContent({ jobDescription: '' });
      const parsedState = editor.parseEditorState(templateResult.content);
      editor.setEditorState(parsedState);
    } catch (error) {
      console.error('Failed to reset to template:', error);
    }
  };

  const undo = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined);
  };

  const redo = () => {
    editor.dispatchCommand(REDO_COMMAND, undefined);
  };

  const formatBlock = (type: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (type === 'paragraph') {
          $setBlocksType(selection, () => $createParagraphNode());
        } else if (type === 'h1' || type === 'h2' || type === 'h3') {
          $setBlocksType(selection, () => $createHeadingNode(type as HeadingTagType));
        }
      }
    });
    setBlockType(type);
    setShowBlockDropdown(false);
  };

  const applyFontFamily = (family: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'font-family': family });
      }
    });
    setFontFamily(family);
    setShowFontDropdown(false);
  };

  const applyFontSize = (size: number) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'font-size': `${size}pt` });
      }
    });
    setFontSize(size);
    setShowSizeDropdown(false);
  };

  const changeFontSize = (delta: number) => {
    const newSize = Math.max(8, Math.min(72, fontSize + delta));
    applyFontSize(newSize);
  };

  const applyTextColor = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { color });
      }
    });
    setTextColor(color);
    setShowTextColorPicker(false);
  };

  const applyHighlightColor = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'background-color': color === 'transparent' ? '' : color });
      }
    });
    setHighlightColor(color);
    setShowHighlightPicker(false);
  };

  const formatAlignment = (alignment: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
    setElementFormat(alignment);
  };

  const applyLineSpacing = (spacing: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { 'line-height': spacing });
      }
    });
    setLineSpacing(spacing);
    setShowLineSpacingDropdown(false);
  };

  const getLineSpacingLabel = () => {
    const found = LINE_SPACING_OPTIONS.find((opt) => opt.value === lineSpacing);
    return found ? found.label : lineSpacing;
  };

  const getBlockLabel = () => {
    const found = BLOCK_TYPES.find((b) => b.value === blockType);
    return found ? found.label : 'Normal';
  };

  const getFontLabel = () => {
    const found = FONT_FAMILIES.find((f) => f.value === fontFamily);
    return found ? found.label : 'Arial';
  };

  return (
    <div className="editor-toolbar">
      {/* Undo / Redo */}
      <div className="toolbar-group">
        <button type="button" className="toolbar-btn" onClick={undo} title="Undo (Ctrl+Z)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7v6h6" />
            <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
          </svg>
        </button>
        <button type="button" className="toolbar-btn" onClick={redo} title="Redo (Ctrl+Y)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 7v6h-6" />
            <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Block Type Dropdown */}
      <div className="toolbar-dropdown" ref={blockDropdownRef}>
        <button
          type="button"
          className="toolbar-dropdown-trigger"
          onClick={() => setShowBlockDropdown(!showBlockDropdown)}
        >
          {getBlockLabel()}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {showBlockDropdown && (
          <div className="toolbar-dropdown-menu toolbar-dropdown-menu--block-types">
            {BLOCK_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className={`toolbar-dropdown-item toolbar-dropdown-item--block ${blockType === type.value ? 'toolbar-dropdown-item--active' : ''}`}
                style={{ fontSize: type.fontSize, fontWeight: type.fontWeight }}
                onClick={() => formatBlock(type.value)}
              >
                {type.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Font Family Dropdown */}
      <div className="toolbar-dropdown" ref={fontDropdownRef}>
        <button
          type="button"
          className="toolbar-dropdown-trigger"
          onClick={() => setShowFontDropdown(!showFontDropdown)}
          style={{ minWidth: '100px' }}
        >
          {getFontLabel()}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {showFontDropdown && (
          <div className="toolbar-dropdown-menu" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {FONT_FAMILIES.map((font) => (
              <button
                key={font.value}
                type="button"
                className={`toolbar-dropdown-item ${fontFamily === font.value ? 'toolbar-dropdown-item--active' : ''}`}
                style={{ fontFamily: font.value }}
                onClick={() => applyFontFamily(font.value)}
              >
                {font.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Font Size Controls */}
      <div className="toolbar-fontsize">
        <button
          type="button"
          className="toolbar-fontsize-btn"
          onClick={() => changeFontSize(-1)}
          title="Decrease font size"
        >
          −
        </button>
        <div className="toolbar-dropdown" ref={sizeDropdownRef}>
          <button
            type="button"
            className="toolbar-fontsize-value"
            onClick={() => setShowSizeDropdown(!showSizeDropdown)}
          >
            {fontSize}
          </button>
          {showSizeDropdown && (
            <div className="toolbar-dropdown-menu" style={{ maxHeight: '200px', overflowY: 'auto', minWidth: '60px' }}>
              {FONT_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={`toolbar-dropdown-item ${fontSize === size ? 'toolbar-dropdown-item--active' : ''}`}
                  onClick={() => applyFontSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="toolbar-fontsize-btn"
          onClick={() => changeFontSize(1)}
          title="Increase font size"
        >
          +
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Text Formatting */}
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${isBold ? 'toolbar-btn--active' : ''}`}
          onClick={formatBold}
          title="Bold (Ctrl+B)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isItalic ? 'toolbar-btn--active' : ''}`}
          onClick={formatItalic}
          title="Italic (Ctrl+I)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="4" x2="10" y2="4" />
            <line x1="14" y1="20" x2="5" y2="20" />
            <line x1="15" y1="4" x2="9" y2="20" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isUnderline ? 'toolbar-btn--active' : ''}`}
          onClick={formatUnderline}
          title="Underline (Ctrl+U)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
            <line x1="4" y1="21" x2="20" y2="21" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isStrikethrough ? 'toolbar-btn--active' : ''}`}
          onClick={formatStrikethrough}
          title="Strikethrough"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="12" x2="20" y2="12" />
            <path d="M17.5 7.5c-.7-1-2-1.5-3.5-1.5-3 0-5 2-5 4 0 1.5 1 2.5 2.5 3" />
            <path d="M8.5 16.5c.7 1 2 1.5 3.5 1.5 3 0 5-2 5-4" />
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Text Color */}
      <div className="toolbar-dropdown" ref={textColorRef}>
        <button
          type="button"
          className="toolbar-color-btn"
          onClick={() => setShowTextColorPicker(!showTextColorPicker)}
          title="Text color"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 20h16" />
            <path d="M12 4L8 16h8L12 4z" />
          </svg>
          <div className="toolbar-color-indicator" style={{ backgroundColor: textColor }} />
        </button>
        {showTextColorPicker && (
          <div className="toolbar-color-picker">
            <div className="toolbar-color-grid">
              {TEXT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`toolbar-color-swatch ${textColor === color ? 'toolbar-color-swatch--active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => applyTextColor(color)}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Highlight Color */}
      <div className="toolbar-dropdown" ref={highlightRef}>
        <button
          type="button"
          className="toolbar-color-btn"
          onClick={() => setShowHighlightPicker(!showHighlightPicker)}
          title="Highlight color"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.243 4.515l-6.738 6.737-.707 2.121-1.04 1.041 2.828 2.829 1.04-1.041 2.122-.707 6.737-6.738-4.242-4.242zm6.364 3.536a1 1 0 010 1.414l-7.778 7.778-2.122.707-1.414 1.414a1 1 0 01-1.414 0l-4.243-4.243a1 1 0 010-1.414l1.414-1.414.707-2.121 7.778-7.778a1 1 0 011.414 0l5.658 5.657z" />
          </svg>
          <div
            className="toolbar-color-indicator"
            style={{ backgroundColor: highlightColor === 'transparent' ? '#e5e7eb' : highlightColor }}
          />
        </button>
        {showHighlightPicker && (
          <div className="toolbar-color-picker">
            <div className="toolbar-color-grid">
              {HIGHLIGHT_COLORS.map((color, index) => (
                <button
                  key={`${color}-${index}`}
                  type="button"
                  className={`toolbar-color-swatch ${highlightColor === color ? 'toolbar-color-swatch--active' : ''}`}
                  style={{
                    backgroundColor: color === 'transparent' ? '#ffffff' : color,
                    backgroundImage: color === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%)' : 'none',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 4px 4px',
                  }}
                  onClick={() => applyHighlightColor(color)}
                  title={color === 'transparent' ? 'None' : color}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Lists */}
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${isBulletList ? 'toolbar-btn--active' : ''}`}
          onClick={formatBulletList}
          title="Bullet List"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <circle cx="4" cy="6" r="1" fill="currentColor" />
            <circle cx="4" cy="12" r="1" fill="currentColor" />
            <circle cx="4" cy="18" r="1" fill="currentColor" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${isNumberedList ? 'toolbar-btn--active' : ''}`}
          onClick={formatNumberedList}
          title="Numbered List"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="10" y1="6" x2="21" y2="6" />
            <line x1="10" y1="12" x2="21" y2="12" />
            <line x1="10" y1="18" x2="21" y2="18" />
            <text x="3" y="8" fontSize="7" fill="currentColor" stroke="none">1</text>
            <text x="3" y="14" fontSize="7" fill="currentColor" stroke="none">2</text>
            <text x="3" y="20" fontSize="7" fill="currentColor" stroke="none">3</text>
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Alignment */}
      <div className="toolbar-align-group">
        <button
          type="button"
          className={`toolbar-btn ${elementFormat === 'left' ? 'toolbar-btn--active' : ''}`}
          onClick={() => formatAlignment('left')}
          title="Align left"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="15" y2="12" />
            <line x1="3" y1="18" x2="18" y2="18" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${elementFormat === 'center' ? 'toolbar-btn--active' : ''}`}
          onClick={() => formatAlignment('center')}
          title="Align center"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="6" y1="12" x2="18" y2="12" />
            <line x1="4" y1="18" x2="20" y2="18" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${elementFormat === 'right' ? 'toolbar-btn--active' : ''}`}
          onClick={() => formatAlignment('right')}
          title="Align right"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="9" y1="12" x2="21" y2="12" />
            <line x1="6" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <button
          type="button"
          className={`toolbar-btn ${elementFormat === 'justify' ? 'toolbar-btn--active' : ''}`}
          onClick={() => formatAlignment('justify')}
          title="Justify"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Line Spacing */}
      <div className="toolbar-dropdown" ref={lineSpacingRef}>
        <button
          type="button"
          className="toolbar-dropdown-trigger toolbar-linespacing-trigger"
          onClick={() => setShowLineSpacingDropdown(!showLineSpacingDropdown)}
          title="Line spacing"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
            <path d="M19 2v4m0 12v4M19 4l-2 2m2-2l2 2M19 20l-2-2m2 2l2-2" />
          </svg>
          <span className="toolbar-linespacing-value">{getLineSpacingLabel()}</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {showLineSpacingDropdown && (
          <div className="toolbar-dropdown-menu" style={{ minWidth: '80px' }}>
            {LINE_SPACING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`toolbar-dropdown-item ${lineSpacing === option.value ? 'toolbar-dropdown-item--active' : ''}`}
                onClick={() => applyLineSpacing(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="toolbar-divider" />

      {/* Link */}
      <div className="toolbar-group">
        <button
          type="button"
          className={`toolbar-btn ${isLink ? 'toolbar-btn--active' : ''}`}
          onClick={insertLink}
          title="Insert Link (Ctrl+K)"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Page Break */}
      <div className="toolbar-group">
        <button
          type="button"
          className="toolbar-btn"
          onClick={insertPageBreak}
          title="Insert Page Break"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16h16" />
            <path d="M4 8h16" />
            <path d="M8 4v4" />
            <path d="M16 4v4" />
            <path d="M8 16v4" />
            <path d="M16 16v4" />
            <line x1="2" y1="12" x2="6" y2="12" strokeDasharray="2 2" />
            <line x1="10" y1="12" x2="14" y2="12" strokeDasharray="2 2" />
            <line x1="18" y1="12" x2="22" y2="12" strokeDasharray="2 2" />
          </svg>
        </button>
      </div>

      <div className="toolbar-divider" />

      {/* Reset to Template */}
      <div className="toolbar-group">
        <button
          type="button"
          className="toolbar-btn toolbar-btn--reset"
          onClick={handleResetClick}
          title="Reset to Template"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="toolbar-reset-overlay">
          <div className="toolbar-reset-dialog">
            <div className="toolbar-reset-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="toolbar-reset-title">Reset to Template?</h3>
            <p className="toolbar-reset-message">All your changes will be lost. This action cannot be undone.</p>
            <div className="toolbar-reset-actions">
              <button
                type="button"
                className="toolbar-reset-btn toolbar-reset-btn--cancel"
                onClick={handleResetCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="toolbar-reset-btn toolbar-reset-btn--confirm"
                onClick={handleResetConfirm}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditorToolbar;
